/**
 * library-shell — THE canonical folder-library shell (design-system pattern
 * "Folder Library"). One implementation of the folders UX for every library
 * surface: Messages, Booking Questions, Surveys, Coupons & Promos, Services
 * mount it — never copy it. Extracted 2026-08-12 (operator) from the
 * Messaging/Questions twins.
 *
 * Pieces:
 *   useFolderLibrary(cfg)  — all folder state + behavior: scope with the
 *     vanished-folder guard, folder-scoped filtering, the search-widen
 *     override, multi-select, drag filing, bulk move, folder chips, and
 *     folder deletion (keep/purge) with the canonical toasts.
 *   <FolderShellFrame lib> — the layout: desktop FolderTree left, folder
 *     cards on phone/tablet, breadcrumb, then the library's own content.
 *   <FolderShellBars lib>  — the "Searching in X · Search all" widen note
 *     and the purple selection bar (Move to… / Remove from folder / Clear).
 *   <FolderShellOverlays lib> — the Folders… label picker, the bulk Move to
 *     picker, and the delete-folder keep/purge modal.
 *
 * Presentation primitives stay in shared/collections-ui; data in
 * lib/collections-api. Both are jrni-ui-token styled.
 */
import React, { useEffect, useState, type ReactNode } from "react";
import { Btn, Icon, T } from "@v0y4ge/jrni-ui";
import { toast } from "../../shell/toast";
import { useViewport } from "../../shell/useResponsive";
import { useT } from "../../lib/i18n";
import { useCollections, type Collections, type Folder } from "../../lib/collections-api";
import { FolderTree, FolderCards, FolderBreadcrumb, FolderPickerPopover, DeleteFolderModal, type FolderScope } from "./collections-ui";

export interface FolderLibraryCfg {
  /** collections family — "messages" | "questions" | "surveys" | "promos" | … */
  family: string;
  /** every item id in the library (unfiltered) */
  ids: string[];
  canWrite: boolean;
  /** "All messages" / "All questions" / … */
  allLabel: string;
  /** "Search all messages" / … for the widen link */
  searchAllLabel: string;
  /** live search text — clearing it snaps the widen override back */
  query: string;
  /** danger-toast title when a filing write fails */
  failTitle: string;
  /** called after a purge delete so the item store can reload */
  onAfterPurge?: () => void;
}

export interface FolderLib {
  col: Collections;
  cfg: FolderLibraryCfg;
  desktop: boolean;
  scope: FolderScope; setScope: (s: FolderScope) => void;
  scopedFolder: string | null;
  scopeName: string;
  wide: boolean; setWide: (v: boolean) => void;
  sel: Set<string>; setSel: (s: Set<string>) => void;
  toggleSel: (id: string, on: boolean) => void;
  inScope: (id: string) => boolean;
  /** ids visible in the current scope (before the library's own filters) */
  pool: string[];
  countFor: (s: FolderScope) => number;
  dropIds: (target: FolderScope, ids: string[]) => Promise<void>;
  dragProps: (id: string) => Record<string, unknown>;
  folderChips: (id: string) => ReactNode;
  openLabelPicker: (id: string, x: number, y: number) => void;
  /** ⋯-menu entry that opens the label picker for an item */
  foldersMenuItem: (id: string) => { icon: ReactNode; label: string; onClick: (e?: unknown) => void };
  treeProps: {
    col: Collections; scope: FolderScope; onScope: (s: FolderScope) => void;
    countFor: (s: FolderScope) => number; canWrite: boolean;
    onDropIds: (target: FolderScope, ids: string[]) => void; allLabel: string;
    onAskDelete: (f: Folder) => void;
  };
  // internal overlay state, consumed by FolderShellBars/Overlays
  _labelFor: { id: string; x: number; y: number } | null; _setLabelFor: (v: { id: string; x: number; y: number } | null) => void;
  _moveAt: { x: number; y: number } | null; _setMoveAt: (v: { x: number; y: number } | null) => void;
  _askDelete: Folder | null; _setAskDelete: (f: Folder | null) => void;
  _delBusy: boolean;
  _bulkMoveTo: (target: string) => Promise<void>;
  _doDeleteFolder: (mode: "keep" | "purge") => Promise<void>;
}

export function useFolderLibrary(cfg: FolderLibraryCfg): FolderLib {
  const t = useT();
  const col = useCollections(cfg.family);
  const desktop = useViewport() === "desktop";
  const [scope, setScope] = useState<FolderScope>("all");
  const [wide, setWide] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [labelFor, setLabelFor] = useState<{ id: string; x: number; y: number } | null>(null);
  const [moveAt, setMoveAt] = useState<{ x: number; y: number } | null>(null);
  const [askDelete, setAskDelete] = useState<Folder | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  // A folder that vanished (tenant switch, deletion elsewhere) must not strand the view.
  useEffect(() => {
    if (scope !== "all" && scope !== "unfiled" && !col.loading && !col.folders.some((f) => f.id === scope)) setScope("all");
  }, [scope, col.loading, col.folders]);
  useEffect(() => { setWide(false); setSel(new Set()); }, [scope]);
  // Widening is a property of the CURRENT search — clearing the query snaps back.
  useEffect(() => { if (!cfg.query.trim()) setWide(false); }, [cfg.query]);

  const filedIds = new Set<string>();
  Object.values(col.members).forEach((arr) => arr.forEach((id) => filedIds.add(id)));
  const inScope = (id: string): boolean =>
    scope === "all" ? true : scope === "unfiled" ? !filedIds.has(id) : col.rollup(scope).has(id);
  const pool = (wide ? cfg.ids : cfg.ids.filter(inScope));
  const countFor = (s: FolderScope): number =>
    s === "all" ? cfg.ids.length : s === "unfiled" ? cfg.ids.filter((id) => !filedIds.has(id)).length : cfg.ids.filter((id) => col.rollup(String(s)).has(id)).length;
  const scopedFolder = scope !== "all" && scope !== "unfiled" ? scope : null;
  const failToast = () => toast({ tone: "danger", title: cfg.failTitle, subtitle: t("collections.retryInAMoment", "Retry in a moment.") });

  const dropIds = async (target: FolderScope, ids: string[]) => {
    try {
      if (target === "unfiled") {
        const byFolder = new Map<string, string[]>();
        ids.forEach((id) => col.foldersOf(id).forEach((fid) => byFolder.set(fid, [...(byFolder.get(fid) || []), id])));
        for (const [fid, list] of byFolder) await col.setItems(fid, { remove: list });
        toast({ tone: "success", title: t("collections.movedToast", "Moved"), subtitle: `${ids.length} → ${t("collections.unfiled", "Unfiled")}` });
      } else if (target !== "all") {
        await col.setItems(String(target), { add: ids });
        if (scopedFolder && scopedFolder !== target) await col.setItems(scopedFolder, { remove: ids });
        const f = col.folders.find((x) => x.id === target);
        toast({ tone: "success", title: scopedFolder ? t("collections.movedToast", "Moved") : t("collections.addedToast", "Added to folder"), subtitle: `${ids.length} → ${f ? f.name : ""}` });
      }
      setSel(new Set());
    } catch (e) { console.warn("filing failed:", e); failToast(); }
  };
  const bulkMoveTo = async (target: string) => {
    const ids = Array.from(sel);
    setMoveAt(null);
    try {
      const byFolder = new Map<string, string[]>();
      ids.forEach((id) => col.foldersOf(id).forEach((fid) => { if (fid !== target) byFolder.set(fid, [...(byFolder.get(fid) || []), id]); }));
      for (const [fid, list] of byFolder) await col.setItems(fid, { remove: list });
      await col.setItems(target, { add: ids });
      const f = col.folders.find((x) => x.id === target);
      toast({ tone: "success", title: t("collections.movedToast", "Moved"), subtitle: `${ids.length} → ${f ? f.name : ""}` });
      setSel(new Set());
    } catch (e) { console.warn("bulk move failed:", e); failToast(); }
  };
  const doDeleteFolder = async (mode: "keep" | "purge") => {
    if (!askDelete) return;
    setDelBusy(true);
    try {
      await col.remove(askDelete.id, mode);
      if (scope === askDelete.id) setScope("all");
      if (mode === "purge") cfg.onAfterPurge?.();
      toast({ tone: "warn", title: t("collections.folderDeleted", "Folder deleted"), subtitle: askDelete.name });
      setAskDelete(null);
    } catch (e) { console.warn("delete folder failed:", e); failToast(); }
    finally { setDelBusy(false); }
  };
  const toggleSel = (id: string, on: boolean) => setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
  const dragProps = (id: string) => cfg.canWrite ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      const ids = sel.has(id) ? Array.from(sel) : [id];
      e.dataTransfer.setData("text/plain", JSON.stringify(ids));
      e.dataTransfer.effectAllowed = "move";
    },
  } : {};
  const folderChips = (id: string): ReactNode => {
    if (scope !== "all" && scope !== "unfiled") return null;
    const fs = col.foldersOf(id).map((fid) => col.folders.find((f) => f.id === fid)).filter(Boolean) as Folder[];
    if (!fs.length) return null;
    return (
      <span style={{ display: "inline-flex", gap: 4, marginLeft: 8 }}>
        {fs.slice(0, 2).map((f) => <span key={f.id} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 600, color: T.soft, background: T.n6, borderRadius: 999, padding: "2px 7px" }}><Icon.folder size={9} />{f.name}</span>)}
        {fs.length > 2 ? <span style={{ fontSize: 10.5, color: T.soft, alignSelf: "center" }}>+{fs.length - 2}</span> : null}
      </span>
    );
  };
  const openLabelPicker = (id: string, x: number, y: number) => setLabelFor({ id, x, y });
  const foldersMenuItem = (id: string) => ({
    icon: <Icon.folder size={14} />,
    label: `${t("collections.folders", "Folders")}\u2026`,
    onClick: (e?: unknown) => { const ev = e as MouseEvent | undefined; openLabelPicker(id, ev?.clientX ?? window.innerWidth / 2, ev?.clientY ?? 200); },
  });
  const scopeName = scope === "all" ? cfg.allLabel : scope === "unfiled" ? t("collections.unfiled", "Unfiled") : (col.folders.find((f) => f.id === scope)?.name ?? "");
  const treeProps = { col, scope, onScope: setScope, countFor, canWrite: cfg.canWrite, onDropIds: dropIds, allLabel: cfg.allLabel, onAskDelete: setAskDelete };

  return {
    col, cfg, desktop, scope, setScope, scopedFolder, scopeName, wide, setWide,
    sel, setSel, toggleSel, inScope, pool, countFor, dropIds, dragProps, folderChips,
    openLabelPicker, foldersMenuItem, treeProps,
    _labelFor: labelFor, _setLabelFor: setLabelFor, _moveAt: moveAt, _setMoveAt: setMoveAt,
    _askDelete: askDelete, _setAskDelete: setAskDelete, _delBusy: delBusy,
    _bulkMoveTo: bulkMoveTo, _doDeleteFolder: doDeleteFolder,
  };
}

/** Layout frame: tree left (desktop), cards + breadcrumb above the content. */
export function FolderShellFrame({ lib, children }: { lib: FolderLib; children: ReactNode }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
      {lib.desktop ? <FolderTree {...lib.treeProps} /> : null}
      <div style={{ flex: 1, minWidth: 0, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column" }}>
        {!lib.desktop ? <FolderCards {...lib.treeProps} parentOf={(s) => { const f = lib.col.folders.find((x) => x.id === s); return f?.parentId ?? "all"; }} /> : null}
        <FolderBreadcrumb col={lib.col} scope={lib.scope} onScope={lib.setScope} allLabel={lib.cfg.allLabel} />
        {children}
      </div>
    </div>
  );
}

/** The widen note + the selection bulk bar. Render between the filter row and the list. */
export function FolderShellBars({ lib }: { lib: FolderLib }) {
  const t = useT();
  return (
    <>
      {lib.cfg.query.trim() && lib.scope !== "all" && !lib.wide ? (
        <div style={{ fontSize: 12.5, color: T.soft, padding: "2px 0 6px" }}>
          {t("collections.searchingIn", "Searching in")} <b style={{ color: T.n1 }}>{lib.scopeName}</b> ·{" "}
          <Btn variant="link" size="sm" onClick={() => lib.setWide(true)}>{lib.cfg.searchAllLabel}</Btn>
        </div>
      ) : null}
      {lib.sel.size ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", margin: "4px 0 6px", borderRadius: 10, background: T.p4, border: `1px solid ${T.p1}22` }}>
          <b style={{ fontSize: 13, color: T.p1 }}>{lib.sel.size} {t("collections.selected", "selected")}</b>
          <Btn size="sm" variant="secondary" icon={<Icon.folder size={13} />} onClick={(e) => lib._setMoveAt({ x: (e as React.MouseEvent).clientX, y: (e as React.MouseEvent).clientY })}>{t("collections.moveTo", "Move to")}…</Btn>
          {lib.scopedFolder ? <Btn size="sm" variant="secondary" onClick={() => { void lib.col.setItems(lib.scopedFolder!, { remove: Array.from(lib.sel) }).then(() => lib.setSel(new Set())); }}>{t("collections.removeFromFolder", "Remove from folder")}</Btn> : null}
          <span style={{ flex: 1 }} />
          <Btn variant="link" size="sm" onClick={() => lib.setSel(new Set())}>{t("collections.clear", "Clear")}</Btn>
        </div>
      ) : null}
    </>
  );
}

/** Label picker, bulk Move to picker, and the delete-folder modal. Render last inside <Screen>. */
export function FolderShellOverlays({ lib }: { lib: FolderLib }) {
  const t = useT();
  return (
    <>
      {lib._labelFor ? (
        <FolderPickerPopover col={lib.col} at={lib._labelFor} mode="labels" title={t("collections.folders", "Folders")}
          checked={new Set(lib.col.foldersOf(lib._labelFor.id))} onClose={() => lib._setLabelFor(null)}
          onToggle={(fid, on) => { void lib.col.setItems(fid, on ? { add: [lib._labelFor!.id] } : { remove: [lib._labelFor!.id] }); }} />
      ) : null}
      {lib._moveAt ? (
        <FolderPickerPopover col={lib.col} at={lib._moveAt} mode="move" title={`${t("collections.moveTo", "Move to")}…`} onClose={() => lib._setMoveAt(null)} onPick={(fid) => void lib._bulkMoveTo(fid)} />
      ) : null}
      {lib._askDelete ? (
        <DeleteFolderModal folder={lib._askDelete} busy={lib._delBusy}
          count={lib.cfg.ids.filter((id) => lib.col.rollup(lib._askDelete!.id).has(id)).length}
          onClose={() => lib._setAskDelete(null)} onKeep={() => void lib._doDeleteFolder("keep")} onPurge={() => void lib._doDeleteFolder("purge")} />
      ) : null}
    </>
  );
}
